import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from '../firebase/config'; // Adjust the import path as necessary
import { signInWithEmailAndPassword, signOut, multiFactor } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        setUser({ ...firebaseUser, ...userData });
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password, otp = null, verificationId = null) => {
    setError(null);
    try {
      if (otp && verificationId) {
        const cred = PhoneAuthProvider.credential(verificationId, otp);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        const userCredential = await window.resolver.resolveSignIn(multiFactorAssertion);
        setUser(userCredential.user);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        setUser({ ...userCredential.user, ...userDoc.data() });
      }
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        window.resolver = err.resolver;
        const recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, auth);
        const phoneInfoOptions = {
          multiFactorHint: err.resolver.hints[0],
          session: err.resolver.session,
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
        return { mfaRequired: true, verificationId };
      }
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const enrollMFA = async (phoneNumber) => {
    const session = await multiFactor(auth.currentUser).getSession();
    const recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, auth);
    const phoneInfoOptions = { phoneNumber, session };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
    return verificationId;
  };

  const verifyMFAEnrollment = async (verificationId, otp) => {
    const cred = PhoneAuthProvider.credential(verificationId, otp);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'Phone');
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, enrollMFA, verifyMFAEnrollment }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);